import { navigate } from "gatsby";

export const navigateSSRWrapper = (url: string) => {
  return typeof window === "undefined" ? Promise.resolve() : navigate(url);
};

export const scrollToElement = (
  element: HTMLElement | null,
  time: number = 600,
  offset: number = 0
): void => {
  if (!element) return;

  const targetY = element.getBoundingClientRect().top + window.scrollY + offset;
  const startY = window.scrollY;
  const diff = targetY - startY;
  const startTime = performance.now();

  const easeInOutCubic = (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const step = (currentTime: number): void => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / time, 1);
    const ease = easeInOutCubic(progress);

    window.scrollTo(0, startY + diff * ease);

    if (elapsed < time) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
};

type ClassValue =
  | string
  | null
  | undefined
  | false
  | ClassValue[]
  | Record<string, boolean>;

const resolveToken = (
  styles: Record<string, string>,
  token: string
): string | undefined => {
  if (!token) return undefined;
  return styles[token] ?? token;
};

const flattenClassValue = (
  styles: Record<string, string>,
  value: ClassValue,
  acc: string[]
): void => {
  if (!value) return;

  if (typeof value === "string") {
    const resolved = resolveToken(styles, value.trim());
    if (resolved) acc.push(resolved);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => flattenClassValue(styles, item, acc));
    return;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, enabled]) => {
      if (enabled) {
        const resolved = resolveToken(styles, key);
        if (resolved) acc.push(resolved);
      }
    });
  }
};

export const classNames = (
  styles: Record<string, string>,
  ...values: ClassValue[]
): string => {
  const resolved: string[] = [];
  values.forEach((value) => flattenClassValue(styles, value, resolved));
  return Array.from(new Set(resolved)).join(" ");
};

export const bindClassNames =
  (styles: Record<string, string>) =>
  (...values: ClassValue[]): string =>
    classNames(styles, ...values);
